import React from "react";
import {
  Button,
  CircularProgress,
  Box,
  useTheme,
  SxProps,
  Theme,
} from "@mui/material";
import { tokens } from "../../theme";

export interface AppButtonProps {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: "contained" | "outlined" | "text" | "gradient";
  color?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "warning"
    | "success"
    | "inherit";
  size?: "small" | "medium" | "large";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  pill?: boolean;
  type?: "button" | "submit" | "reset";
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
}

const AppButton: React.FC<AppButtonProps> = ({
  children,
  onClick,
  variant = "contained",
  color = "primary",
  size = "medium",
  startIcon,
  endIcon,
  disabled = false,
  isLoading = false,
  loadingText = "Please wait...",
  fullWidth = false,
  pill = false,
  type = "button",
  autoFocus = false,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const appTheme = tokens(theme.palette.mode);

  const isGradient = variant === "gradient";
  const muiVariant = isGradient ? "contained" : variant;

  const gradientStyle = isGradient
    ? {
        background:
          appTheme.gradients[color as keyof typeof appTheme.gradients] ||
          appTheme.gradients.primary,
        color: "#ffffff",
        border: "none",
        boxShadow: theme.shadows[3],
        "&:hover": {
          boxShadow: theme.shadows[6],
          transform: "translateY(-1px)",
          filter: "brightness(1.1)",
        },
        "&:active": {
          transform: "translateY(0px)",
          boxShadow: theme.shadows[2],
        },
        "&.Mui-disabled": {
          background:
            appTheme.gradients[color as keyof typeof appTheme.gradients] ||
            appTheme.gradients.primary,
          opacity: 0.6,
          color: "#ffffff",
          cursor: "not-allowed !important",
          pointerEvents: "all !important",
        },
      }
    : {};

  return (
    <Button
      type={type}
      onClick={onClick}
      autoFocus={autoFocus}
      variant={muiVariant}
      color={color}
      size={size}
      disabled={disabled || isLoading}
      fullWidth={fullWidth}
      startIcon={!isLoading ? startIcon : null}
      endIcon={!isLoading ? endIcon : null}
      disableElevation={isGradient}
      sx={{
        borderRadius: pill ? "50px" : theme.appRadius.sm,
        fontWeight: 600,
        textTransform: "none",
        minHeight:
          size === "large"
            ? 48
            : size === "small"
              ? 32
              : theme.appSizes.buttonHeightMedium,
        px: variant === "text" ? 1.5 : 3,
        transition: "all 0.2s ease-in-out",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "flex",
        "& .MuiButton-startIcon": {
          flexShrink: 0,
        },
        "& .MuiButton-endIcon": {
          flexShrink: 0,
        },
        "&.Mui-disabled": {
          cursor: "not-allowed !important",
          pointerEvents: "all !important",
        },
        ...gradientStyle,
        ...(variant === "outlined" && {
          borderWidth: "1.5px",
          "&:hover": { borderWidth: "1.5px" },
        }),
        ...(variant === "text" && {
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.05)",
          },
        }),
        ...sx,
      }}
      {...props}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={20} color="inherit" thickness={4} />
          {loadingText && <span>{loadingText}</span>}
        </Box>
      ) : (
        children
      )}
    </Button>
  );
};

export default AppButton;
